import { useAPI } from '~/composables/useApi'
import type { IRequestResponse } from '~/types'
import type { PricingCardPattern } from '~/components/products/min-benefit-card.vue'
import type { IconLibProps } from '~/components/Tools/icon.vue'

export type ProductContext = Array<{
	id: string
	name: string
	code_name: number
	product_color: string
	product_category_id: string
	mca_product_id: string
	benefits: unknown
	link_name?: string
	renewable: boolean
	claimable: boolean
	form_name: string
	market_price: string | number
	is_dynamic_price: boolean
	inspectable: boolean
	certificateable: boolean
	active: boolean
	need_to_know?: Array<unknown>
	others: unknown
	exclusions?: Array<string>
	how_to_claim?: Array<string>
	created_by: string
	updated_by: string
	supporting_document: unknown
	short_benefits?: Array<string>
	how_to_get_covered?: Array<string>
	alternate_id?: string
	alternative_price?: number | string
	provider_percentage: number
	provider_id: string | null
	underwriter: string
	hospital_info?: string
	show: boolean
	alternate_name: string
	short_description?: string
	description: unknown
	created_at: string
	updated_at: string
	deleted_at: string | null
	group_id: string
	price: ProductPriceContext
	prices: Array<ProductPriceContext>
	productCategory: {
		id: string
		name: string
		description: string
		active: boolean
	}
	pricingBenefits?: {
		name: {
			text: string
		}
		description: {
			text: string
		}
		price?: {
			text: string | number
			ui?: string
		}
		tag?: {
			text?: string[]
			ui?: string
		}
		benefit_highlight?: Array<{
			text: string
			notAllowed?: boolean
		}>
		dedicated_benfit?: PricingCardPattern['benefit_highlight']
		individual_benefit?: PricingCardPattern['benefit_highlight']
		card_icon?: {
			onhover?: IconLibProps
			default?: IconLibProps
		}
		actions?: {
			text: string
			ui?: string
		}
	}
}>
export type ProductReturnResponse = {
	groupedByName: GroupProductByNameContext
	productList: ProductContext
}
export type GroupProductByNameContext = Record<
	string,
	{
		category: string
		product_name: string
		code_name: number
		list: ProductContext | null
		pricingBenefits?: ProductContext[number]['pricingBenefits']
	}
>
export type DropdownItemContext = Array<{
	ui?: string
	title: LabelContext
	collection: Array<LabelContext>
}>
export type LabelContext = {
	text: string
	extension?: string
	ui?: string
}
export type BenefitGroupedData = {
	[id: string | number]: Array<{
		title: string | { text: string }
		description: { text: string; check?: boolean }
		list?: Array<{
			label: Partial<LabelContext>
			collection: Partial<LabelContext>[]
			description?: Partial<LabelContext & { check: boolean; excluded: boolean }>
		}>
		provider?: ProductProviders
	}>
}

export function useProductsApi() {
	const baseURL = String(import.meta.env.VITE_LANDING_PAGE_API)

	const getProductList = async <T = ProductReturnResponse['groupedByName']>(
		query?: { category?: string; entity_id?: string; excludes?: string[] },
		transform = true
	) => {
		const result = await useAPI<IRequestResponse<T>>('/product-list', {
			baseURL,
			query: { ...query, user_type: UserTypes.BUSINESS },
			onResponse({ response }) {
				if (response._data?.data && transform) {
					response._data = {
						...response._data,
						data: response._data.data?.groupedByName || response._data.data?.productList
					}
				}
			}
		})
		return result.data.value?.data || null
	}

	const getCategoriesList = async (query?: Record<'category' | 'user_type' | string, string>) => {
		const result = await useAPI<IRequestResponse<ProductCategoryResponse[]>>('/product-categories', {
			baseURL,
			method: 'GET',
			query: { ...query, user_type: UserTypes.BUSINESS }
		})
		return result?.data.value?.data || []
	}

	const groupedProductsByCategory = async (query?: Record<'category' | 'user_type' | string, string>) => {
		const result = await useAPI<IRequestResponse<Record<string, ProductContext>>>('/grouped-products-by-category', {
			baseURL,

			query: { ...query, user_type: UserTypes.BUSINESS },
			onResponse({ response }) {
				if (response._data?.data) {
					response._data = {
						...response._data,
						data: response._data.data.groupedProducts
					}
				}
			}
		})
		return result.data.value?.data || null
	}
	// Get product benefit
	type ProductParams = {
		code_name: number
		user_type: UserTypes
		category?: string
		params?: number
		provider?: string
	}
	const getProductBenefit = async (opt: ProductParams) => {
		const { params, ...query } = opt
		const { data } = await useAPI<IRequestResponse<BenefitGroupedData>>(`${baseURL}/benefits/${params || ''}`, {
			query
		})
		return data.value?.data ?? []
	}
	/**
	 *  @deprecated Use getProductInformation instead
	 */
	const getProductWorks = async (query: Omit<ProductParams, 'params'>) => {
		const { data } = await useAPI<IRequestResponse<BenefitGroupedData>>(`${baseURL}/how-it-works`, {
			query
		})
		return data.value?.data ?? []
	}
	/**
	 *  @deprecated Use getProductInformation instead
	 */
	const getProductClaimProcess = async (query: Omit<ProductParams, 'params'>) => {
		const { data } = await useAPI<IRequestResponse<BenefitGroupedData>>(`${baseURL}/claim-process`, {
			query
		})
		return data.value?.data ?? []
	}
	/**
	 *  @deprecated Use getProductInformation instead
	 */
	const getProductNeedToKnow = async (query: Omit<ProductParams, 'params'>) => {
		const { data } = await useAPI<IRequestResponse<BenefitGroupedData>>(`${baseURL}/product-need-to-know`, {
			query
		})
		return data.value?.data ?? []
	}
	const getProductInformation = async (query: Omit<ProductParams, 'params'>) => {
		const { data } = await useAPI<IRequestResponse<BenefitGroupedData>>(`${baseURL}/product-information`, {
			query
		})
		return data.value
	}

	return {
		getProductInformation,
		groupedProductsByCategory,
		getProductList,
		getCategoriesList,
		getProductBenefit,
		getProductWorks,
		getProductClaimProcess,
		getProductNeedToKnow,
		allProducts: async (payload?: string) => {
			const { data, error } = await useAPI<IRequestResponse<ProductsResponse[]>>(`/products/${payload || ''}`, {
				query: {
					user_type: UserTypes.BUSINESS
				}
			})
			const result = data.value
			const { Benefits } = useProductBenefit()
			const { currencyToNaira } = useCurrency()
			const { toLower } = useUtils()
			if (!error.value && result?.data && Array.isArray(result.data)) {
				result.data = result.data
					.map(res => {
						let market_price = '0' as string | number
						let is_dynamic_price = false
						// Get the price of the first product in the array
						res.prices = res.prices.sort((a, b) => parseFloat(String(a.market_price)) - parseFloat(String(b.market_price)))

						// Makes sure the market price variations exists in the response data
						if (res?.prices && Array.isArray(res.prices)) {
							const monthlyPrice = res.prices.find(price => {
								const period = toLower(price.cover_period)
								if (period === 'monthly' || period === 'bi-annual') {
									return price
								} else if (period === 'annual' && price.preferred) {
									return price
								}
								return null
							})
							// Get the price of the first product in the array
							market_price = monthlyPrice?.market_price ?? 0
							is_dynamic_price = monthlyPrice?.is_dynamic_price ?? false
						}
						let product_price = currencyToNaira(market_price || '0')
						if (is_dynamic_price && market_price) {
							product_price = parseFloat(market_price as string) + '%'
						}
						const form_name = String(res?.alternate_name ?? '')
							.replace(/ /g, '_')
							.toLowerCase()

						return {
							...res,
							form_name,
							market_price,
							is_dynamic_price,
							desc: String(
								Benefits?.[res.code_name]?.card_label ?? 'Benefit Not Available yet...'
							).replace('mcg_price', product_price)
						}
					})
					.sort((a, b) => {
						const aMinPrice = Math.min(...a.prices.map(price => parseFloat(String(price.market_price))))
						const bMinPrice = Math.min(...b.prices.map(price => parseFloat(String(price.market_price))))
						return aMinPrice - bMinPrice
					})
			}
			return result
		},
		getOneProduct: async (productId: string) => {
			const { data } = await useAPI<IRequestResponse<ProductsResponse>>(`/products/${productId}`, {
				query: {
					user_type: UserTypes.BUSINESS
				}
			})
			let result = data.value?.data
			if (result && Object.values(result).length) {
				result = { ...result, form_name: result.alternate_name?.replace(/ /g, '_')?.toLowerCase() }
				let price = null
				let prices: ProductsResponse['prices'] = []
				// Makes sure the market price variations exists in the response data
				if (result?.prices && Array.isArray(result.prices)) {
					const { toLower } = useUtils()
					const isHealth = toLower(result.productCategory.name) === 'health'
					if (isHealth) {
						prices = result.prices.filter(price => {
							const period = toLower(price.cover_period)
							return period === 'annual' || period === 'bi-annual'
						})
					} else {
						prices = result.prices
					}

					// Get the price of the first product in the array
					price = prices
						.map(p => {
							return {
								...p,
								market_price: isHealth
									? parseInt(String(p.market_price)) * Number(p.month)
									: p.market_price,
								genius_price: isHealth
									? parseInt(String(p.genius_price)) * Number(p.month)
									: p.genius_price
							}
						})
						.find(p => {
							const period = toLower(p.cover_period)
							return p.preferred || period === 'bi-annual'
						})
				}
				if (price) {
					result = { ...result, price }
				}
			}
			return result
		},
		getProductCategories: async (payload?: string) => {
			const { data } = await useAPI<IRequestResponse<ProductCategoryResponse[]>>(
				`/products/categories/${payload || ''}`
			)
			return data.value
		},
		getProductPriceByCoverPeriod: async (productId: string, cover_period: string) => {
			const { data } = await useAPI<IRequestResponse<ProductPriceResponse>>(
				`/products/fetch-price-by-cover-period?product_id=${productId}&cover_period=${cover_period || ''}`
			)
			return data.value
		},
		getProductPriceByMonth: async (productId: string, cover_period: number | string) => {
			const { data } = await useAPI<IRequestResponse<ProductPriceResponse>>(
				`/products/fetch-price-by-month?product_id=${productId}&month=${cover_period || ''}`
			)
			return data.value
		}
	}
}
